
import { useState, useEffect, Fragment } from "react";
import Table from 'react-bootstrap/Table';
import Image from 'react-bootstrap/Image';
import { Button } from "react-bootstrap";
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';

import { getDataHelper, postDataWithHeaderHelper, uploadFileHelper } from "../requests/PostRequests";
import NavBar from "../components/NavBar";
import Header from "../components/Header";
import { ObjectStatus } from "../components/ObjectStatus";
import { DeleteConfirmation } from "../components/DeleteConfirmation";

const Resources = ({ onTriggerMessage }) => {

  const newUserGuideObject = {
    id: null,
    title: "",
    image_url: "",
    image_local: false,
    image_file: null,
    description: "",
    status: ObjectStatus.New,
    user_guide_resources: []
  };

  const [userGuides, setUserGuides] = useState([]);
  let formValues = {};

  // Current object for editing
  const [currentUserGuide, setCurrentUserGuide] = useState(newUserGuideObject);

  // For displaying the form
  const [userGuideFormShowFlag, setUserGuideFormShowFlag] = useState(false);
  const userGuideFormClose = () => setUserGuideFormShowFlag(false);
  const userGuideFormShow = () => setUserGuideFormShowFlag(true);

  // For displaying the delete confirmation
  const [deleteUserGuideModalShowFlag, setDeleteUserGuideModalShowFlag] = useState(false);
  const deleteUserGuideModalClose = () => setDeleteUserGuideModalShowFlag(false);
  const deleteUserGuideModalShow = () => setDeleteUserGuideModalShowFlag(true);

  // For displaying resource rows
  const [userGuideSparseMap, setUserGuideSparseMap] = useState({});
  const [showResourceTableFlag, setShowResourceTableFlag] = useState({});

  /**
   * Get all the user guides from the server and update this page
   * @param {*} newUserGuide 
   */
  async function getUserGuides() {
    const onSuccess = (data) => {
      let newUserGuides = [];
      let newUserGuideFlag = {};
      let newUserGuideIndexing = {};
      const rows = data.result.rows;
      const length = rows.length;

      for (let i = 0; i < length; i++) {
        let userGuideResources = [];
        const resourceLength = rows[i].user_guide_resources.length;
        for (let j = 0; j < resourceLength; j++) {
          userGuideResources.push({
            id: rows[i].user_guide_resources[j].id,
            name: rows[i].user_guide_resources[j].name,
            url: rows[i].user_guide_resources[j].url,
            status: ObjectStatus.Sync,
            old_index: userGuideResources.length,
          });
        }

        const id = parseInt(rows[i].id);
        newUserGuides.push({
          id: id,
          title: rows[i].title,
          image_url: rows[i].image_url,
          description: rows[i].description,
          user_guide_resources: userGuideResources,
          status: ObjectStatus.Sync,
        });

        newUserGuideIndexing[id] = newUserGuides.length - 1;
      }

      setUserGuides(newUserGuides.sort((a, b) => a.id - b.id));

      newUserGuides.forEach(function (resource) {
        newUserGuideFlag[resource.id] = false;
      });

      setShowResourceTableFlag(newUserGuideFlag);
      setUserGuideSparseMap(newUserGuideIndexing);
    };

    await getDataHelper("/v1/user-guides", onTriggerMessage, onSuccess);
  }

  /**
   * Upload User Guide image to the server based on the input resource
   * @param {*} userGuide 
   */
  async function uploadUserGuideImage(userGuide) {
    const onSuccess = (data) => {
      let updatedUserGuide = Object.create(userGuide);
      updatedUserGuide.status = ObjectStatus.Updated;
      updatedUserGuide.image_url = data.result.rows[0].url;
      setCurrentUserGuide(updatedUserGuide);

      onTriggerMessage(
        "Success",
        "Successfully uploaded user guide image to the server",
      );
    };

    // Upload images
    await uploadFileHelper("/v1/user-guides/user-guide/upload",
      {
        file_type: 'images',
        category: 'user-guides',
        owner: 'tmp',
        name: userGuide.image_url,
        file: userGuide.image_file
      }, onTriggerMessage, onSuccess);
  }

  /**
   * Upload User Guide Resource file to the server based on the input
   * @param {*} userGuide 
   * @param {*} index
   */
  async function uploadResourceFile(userGuide, index) {
    const onSuccess = (data) => {
      let updatedUserGuide = Object.create(userGuide);
      userGuide.user_guide_resources[index].status =
        userGuide.user_guide_resources[index].status != ObjectStatus.New
          ? ObjectStatus.Updated
          : ObjectStatus.New;
      userGuide.user_guide_resources[index].file_local = true;
      userGuide.user_guide_resources[index].url = data.result.rows[0].url;
      setCurrentUserGuide(updatedUserGuide);

      onTriggerMessage(
        "Success",
        "Successfully uploaded resource file to the server",
      );
    };

    // Upload images
    await uploadFileHelper("/v1/user-guides/resource/upload",
      {
        file_type: 'files',
        category: 'user-guides',
        owner: 'tmp',
        name: userGuide.user_guide_resources[index].url,
        file_local: userGuide.user_guide_resources[index].file_local,
        file: userGuide.user_guide_resources[index].file
      }, onTriggerMessage, onSuccess);
  }

  // Run upon instantiation
  useEffect(() => {
    async function fetchData() {
      await getUserGuides();
    }

    fetchData();
  }, []);

  /**
   * Convert current form input and apply it onto the currentUserGuide so
   * we will have the latest userGuide information
   * @returns latest userGuide
   */
  function getLatestUserGuideBasedOnForm() {
    /// Convert and merge form values into currentUserGuide
    const conversionMap = {
      'id': 'id',
      'title': 'title',
      'description': 'description',
      'imageUrl': 'image_url',
      'imageFile': 'image_file',
      'imageLocal': 'image_local',
    };

    let newUserGuide = Object.create(currentUserGuide);
    // We want to merge formValues with currentUserGuide so it does not get lost
    Object.entries(formValues).forEach(([key, value]) => {
      if (key.startsWith('resource')) {
        const tokens = key.split('.');
        const index = parseInt(tokens[1]);
        newUserGuide.user_guide_resources[index][tokens[2]] = value;
        newUserGuide.user_guide_resources[index].status =
          newUserGuide.user_guide_resources[index].status != ObjectStatus.New
            ? ObjectStatus.Updated
            : ObjectStatus.New;
        return;
      }

      newUserGuide[conversionMap[key]] = value;
    });

    return newUserGuide;
  }

  function saveFormToCurrentResource() {
    /// Save current form content to currentUserGuide so that any refresh behaviour does not lose changes
    const newUserGuide = getLatestUserGuideBasedOnForm();
    setCurrentUserGuide(newUserGuide);
  }

  function onDeleteUserGuideConfirmation() {
    deleteUserGuideModalClose();
    onDeleteUserGuideToApi(currentUserGuide.id);
  }

  function onAddResource() {
    const emptyResource = {
      name: "",
      url: "",
      status: ObjectStatus.New,
      old_index: Math.max(...currentUserGuide.user_guide_resources.map(r => r.old_index + 1), 0)
    };
    // We are doing this to prevent shallow copy of user_guide_resources
    // else, when we add new resource on the Modal, it will update the original `currentUserGuide`'s user_guide_resources
    // Make sure we convert the form values we have before applying any changes to currentUserGuide
    const userGuide = getLatestUserGuideBasedOnForm();
    const newResourceList = [...userGuide.user_guide_resources, emptyResource];
    let newUserGuide = Object.create(userGuide);
    newUserGuide.user_guide_resources = newResourceList;
    setCurrentUserGuide(newUserGuide);
  }

  function onDeleteResource(removeIndex) {
    // Make sure we convert the form values we have before applying any changes to currentUserGuide
    const userGuide = getLatestUserGuideBasedOnForm();
    let newUserGuide = Object.create(userGuide);
    let tempObject = userGuide.user_guide_resources.find((_, index) => index === removeIndex);
    tempObject.status = ObjectStatus.Deleted;
    newUserGuide.user_guide_resources[removeIndex] = tempObject;
    setCurrentUserGuide(newUserGuide);
  }

  function updateCurrentUserGuideImage(filePath) {
    if (filePath == undefined) {
      return;
    }

    const filename = filePath[0].name;
    const ext = filename.split('.').pop();
    const acceptedExts = ["png", "jpeg"];
    if (!acceptedExts.includes(ext)) {
      saveFormToCurrentResource();
      onTriggerMessage(
        "Invalid file extension",
        `We do not accept this file extension ${ext}, accepted formats: ${acceptedExts}`,
        "danger"
      );
      return;
    }

    let newUserGuide = Object.create(getLatestUserGuideBasedOnForm());
    newUserGuide.status = ObjectStatus.Updated;
    newUserGuide.image_local = true;
    newUserGuide.image_url = filename;
    newUserGuide.image_file = filePath[0];
    // try to upload file to tmp location
    uploadUserGuideImage(newUserGuide);
  }

  function updateCurrentResourceFile(index, filePath) {
    if (filePath == undefined) {
      return;
    }

    const filename = filePath[0].name;

    let newUserGuide = Object.create(getLatestUserGuideBasedOnForm());
    newUserGuide.status = ObjectStatus.Updated;
    newUserGuide.user_guide_resources[index].url = filename;
    newUserGuide.user_guide_resources[index].file = filePath[0];
    newUserGuide.user_guide_resources[index].file_local = true;
    // try to upload file to tmp location
    uploadResourceFile(newUserGuide, index);
  }

  function onUserGuideChangeHandler(e) {
    formValues = { ...formValues, [e.target.name]: e.target.value };
  }

  function UserGuideForm({ onCreate, onUpdate }) {
    const hasImageSelected = currentUserGuide.image_url;
    const onSave = currentUserGuide.id == null ? onCreate : onUpdate;
    return (
      <Modal size="lg" centered backdrop="static" keyboard={false} show={userGuideFormShowFlag} onHide={userGuideFormClose}>
        <Modal.Header closeButton>
          <Modal.Title>{currentUserGuide.id == null ? "New Resource" : currentUserGuide.title}</Modal.Title>
        </Modal.Header>
        <Form>
          <Modal.Body>
            {/* Set the title */}
            <Form.Group
              className="mb-3"
              controlId="userGuideForm.title"
            >
              <Form.Label>Title</Form.Label>
              <Form.Control name="title" defaultValue={currentUserGuide.title} placeholder="User Guide" onChange={onUserGuideChangeHandler} />
            </Form.Group>
            {/* Set the description */}
            <Form.Group
              className="mb-3"
              controlId="userGuideForm.description"
            >
              <Form.Label>Description</Form.Label>
              <Form.Control name="description" defaultValue={currentUserGuide.description} placeholder="This user guide provide information on the website..." as="textarea" rows={3} onChange={onUserGuideChangeHandler} />
            </Form.Group>
            {/* Image Preview */}
            <Image src={currentUserGuide.image_url} style={{ height: "5vw", width: "5vw" }} rounded />
            {/* Setting Image */}
            <Form.Group
              className="mb-3"
              controlId="userGuideForm.imageUrl"
            >
              <Form.Label>Image URL</Form.Label>
              <Form.Control name="imageUrl" defaultValue={currentUserGuide.image_url} disabled={hasImageSelected} placeholder="http://localhost:3102/images/user-guides/1/p0.png" onChange={onUserGuideChangeHandler} />
              <Form.Label>File Upload</Form.Label>
              <Form.Control name="imageFile" type="file" onChange={(e) => updateCurrentUserGuideImage(e.target.files)} />
            </Form.Group>
            {/* Managing individual userGuides */}
            <Table responsive>
              <thead>
                <tr key={`${currentUserGuide.id}_add_new_resource_header_row`} style={{ 'borderBottomWidth': 1 }}>
                  <th>Name</th>
                  <th>URL</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {
                  currentUserGuide.user_guide_resources.length > 0 ? currentUserGuide.user_guide_resources.map(function (item, index) {
                    return (
                      <Fragment key={`${currentUserGuide.id}_${index}_resource_fragment_container`}>
                        {/* We only want to show items that are not deleted  */}
                        {item.status !== ObjectStatus.Deleted ?
                          <Fragment key={`${currentUserGuide.id}_${index}_form_resource_fragment`}>
                            <tr key={`${currentUserGuide.id}_${index}_new_resource_row`} style={{ 'borderBottomWidth': 0 }}>
                              {/* Managing resource name */}
                              <td>
                                <Form.Group
                                  className="mb-3"
                                  controlId={`resource_${index}.name`}
                                >
                                  <Form.Control name={`resource.${index}.name`} defaultValue={item.name} placeholder="Name" onChange={onUserGuideChangeHandler} />
                                </Form.Group>
                              </td>
                              {/* Managing resource link */}
                              <td>
                                <Form.Group
                                  className="mb-3"
                                  controlId={`resource_${index}.url`}
                                >
                                  <Form.Control name={`resource.${index}.url`} defaultValue={item.url} placeholder="http://localhost:3102/images/user-guides/1/p0.png" onChange={onUserGuideChangeHandler} />
                                </Form.Group>
                              </td>
                              {/* Delete resource */}
                              <td><Button variant="danger" onClick={() => onDeleteResource(index)}>Delete</Button></td>
                            </tr>
                            <tr>
                              <td colSpan={3} style={{ 'borderBottomWidth': 1 }}>
                                <Form.Group
                                  className="mb-3"
                                  controlId={`resource_${index}.file`}
                                >
                                  <Form.Control name={`resource.${index}.file`} type="file" onChange={(e) => updateCurrentResourceFile(index, e.target.files)} />
                                </Form.Group>
                              </td>
                            </tr>
                          </Fragment>
                          :
                          <Fragment key={`${currentUserGuide.id}_${index}_deleted_resource_fragment`}></Fragment>
                        }
                      </Fragment>
                    );
                  }) : <></>
                }
              </tbody>
            </Table>
            <Button onClick={onAddResource}>Add New Resource</Button>
          </Modal.Body >
          {/* User should be able to close or save currentResources changes */}
          <Modal.Footer>
            <Button variant="secondary" onClick={userGuideFormClose}>
              Close
            </Button>
            <Button variant="primary" onClick={onSave}>
              Save Changes
            </Button>
          </Modal.Footer>
        </Form>
      </Modal >
    );
  }

  function ResourceRows({ id }) {
    const userGuideResources = userGuides[userGuideSparseMap[id]].user_guide_resources;
    return (
      <Fragment key={`${id}_resource_row_fragment`}>
        <tr key={`${id}_resource_header_row`}>
          <th colSpan={8}>User Guide Resources</th>
        </tr>
        {
          userGuideResources.map(function (item, _) {
            return (
              <Fragment key={`${id}_${item.id}_resource_item_row_fragment`}>
                <tr key={`${id}_${item.id}_resource_row`}>
                  <td colSpan={2}>{item.id}</td>
                  <td colSpan={1}>{item.name}</td>
                  <td colSpan={5}><a href={item.url} target="_blank">{item.url}</a></td>
                </tr>
              </Fragment>
            );
          })
        }
      </Fragment>
    );
  }

  async function postUserGuide(userGuide) {
    const onSuccess = (data) => {
      const rows = data.result.rows;
      const newUserGuideId = rows[0].user_guide_id;

      let updatedUserGuide = Object.create(userGuide);
      updatedUserGuide.status = ObjectStatus.Sync;
      updatedUserGuide.id = newUserGuideId;
      updatedUserGuide.image_url = rows[0].image_url;

      const resourceLength = rows[0].resources.length;
      for (let i = 0; i < resourceLength; i++) {
        const oldIndex = rows[0].resources[i].old_index;
        // Update the index of the created user guide resource based on the index of the resource in userGuide
        updatedUserGuide.user_guide_resources[oldIndex].id = rows[0].resources[i].id;
        updatedUserGuide.user_guide_resources[oldIndex].url = rows[0].resources[i].url;
      }

      setCurrentUserGuide(newUserGuideObject);
      setShowResourceTableFlag({ ...showResourceTableFlag, [newUserGuideId]: false });
      setUserGuideSparseMap({ ...userGuideSparseMap, [newUserGuideId]: userGuides.length });
      setUserGuides([...userGuides, updatedUserGuide]);

      onTriggerMessage(
        "Success",
        "Successfully uploaded user guide to the server",
      );

      userGuideFormClose();
    };

    // Upload user guide
    await postDataWithHeaderHelper("/v1/user-guides/user-guide/add", {
      title: userGuide.title,
      description: userGuide.description,
      image_local: userGuide.image_local,
      image_url: userGuide.image_url,
      resources: userGuide.user_guide_resources.filter((r) => r.status != ObjectStatus.Deleted),
    }, onTriggerMessage, onSuccess);
  }

  async function postDeleteUserGuide(id) {
    const onSuccess = (_) => {
      userGuides[userGuideSparseMap[id]].status = ObjectStatus.Deleted;
      setUserGuides([...userGuides]);
      setCurrentUserGuide(newUserGuideObject);

      onTriggerMessage(
        "Success",
        "Successfully deleted user guide from the server",
      );
    };

    // Upload user guide
    await postDataWithHeaderHelper("/v1/user-guides/user-guide/delete", {
      id: id
    }, onTriggerMessage, onSuccess);
  }

  async function postUpdateUserGuide(userGuide) {
    const onSuccess = (data) => {
      const result = data.result.rows[0];

      let updatedUserGuide = Object.create(userGuide);
      updatedUserGuide.status = ObjectStatus.Sync;
      updatedUserGuide.image_url = result.newImageUrl;

      const resourceLength = result.resources.length;
      for (let i = 0; i < resourceLength; i++) {
        const oldIndex = result.resources[i].old_index;
        // Update the index of the created user guide resource based on the index of the resource in userGuide
        updatedUserGuide.user_guide_resources[oldIndex].id = result.resources[i].id;
        updatedUserGuide.user_guide_resources[oldIndex].url = result.resources[i].url;
      }

      userGuides[userGuideSparseMap[userGuide.id]] = updatedUserGuide;
      setUserGuides([...userGuides]);

      const numOfFailedResource = result.failedResource;
      const numOfSuccessResource = result.successResource;

      onTriggerMessage(
        "Success",
        `Successfully updated user guide on the server 
            with success resource update (${numOfSuccessResource}) 
            and failed resource update (${numOfFailedResource})`,
      );
    };

    // Upload user guide
    await postDataWithHeaderHelper("/v1/user-guides/user-guide/update", {
      id: userGuide.id,
      title: userGuide.title,
      description: userGuide.description,
      image_local: userGuide.image_local,
      image_url: userGuide.image_url,
      newResources: userGuide.user_guide_resources.filter((r) => r.status == ObjectStatus.New),
      updatedResources: userGuide.user_guide_resources.filter((r) => r.status == ObjectStatus.Updated),
      deletedResources: userGuide.user_guide_resources.filter((r) => r.status == ObjectStatus.Deleted),
    }, onTriggerMessage, onSuccess);
  }

  function onAddUserGuide() {
    setCurrentUserGuide(Object.create(newUserGuideObject));
    userGuideFormShow();
  }

  function onEditUserGuide(id) {
    const tempObject = userGuides.find(resource => resource.id == id);
    setCurrentUserGuide(Object.create(tempObject));
    userGuideFormShow();
  }

  function onDeleteUserGuide(id) {
    const tempObject = userGuides.find(resource => resource.id == id);
    setCurrentUserGuide(Object.create(tempObject));
    deleteUserGuideModalShow();
  }

  function toggleResourceTable(id) {
    showResourceTableFlag[id] = !showResourceTableFlag[id];
    setShowResourceTableFlag(Object.create(showResourceTableFlag));
  }

  async function onCreateUserGuideToApi() {
    const latestUserGuide = getLatestUserGuideBasedOnForm();
    await postUserGuide(latestUserGuide);
  }

  async function onUpdateUserGuideToApi() {
    const latestUserGuide = getLatestUserGuideBasedOnForm();
    await postUpdateUserGuide(latestUserGuide);
  }

  async function onDeleteUserGuideToApi(id) {
    await postDeleteUserGuide(id);
  }

  return (
    <>
      <NavBar />
      <div className="container-content-fixed">
        <div className="courses-hdr-grp">
          <Header title="Resources" />
          <Button onClick={onAddUserGuide}>Add New User Guide</Button>
        </div>
        <Table responsive="sm">
          <tbody>
            <tr key="user_guide_header_row">
              <th colSpan={2}>ID</th>
              <th>Title</th>
              <th>Description</th>
              <th>Image URL</th>
              <th>Number of links</th>
              <th>Edit</th>
              <th>Delete</th>
            </tr>
            <tr key="user_guide_header_blank_row"><td colSpan={8}></td></tr>
            {userGuides.map(function (item, index) {
              return (
                <Fragment key={`${item.id}_${index}_main_fragment`}>
                  {item.status !== ObjectStatus.Deleted
                    ? <Fragment key={`${item.id}_fragment`}>
                      <tr key={`${item.id}_user_guide_title_row`}><th colSpan={8}>{item.title}</th></tr>
                      <tr key={`${item.id}_guide_row`} onClick={() => toggleResourceTable(item.id)}>
                        <td colSpan={2}>{item.id}</td>
                        <td>{item.title}</td>
                        <td>{item.description}</td>
                        <td><Image src={item.image_url} style={{ height: "5vw", width: "5vw" }} rounded /></td>
                        <td>{item.user_guide_resources.length}</td>
                        <td><Button variant="primary" onClick={() => onEditUserGuide(item.id)}>Edit</Button></td>
                        <td><Button variant="danger" onClick={() => onDeleteUserGuide(item.id)}>Delete</Button></td>
                      </tr>
                      {showResourceTableFlag[item.id] ? <ResourceRows id={item.id} /> : <></>}
                      <tr key={`${item.id}_user_guide_blank_row`}><td colSpan={8}></td></tr>
                    </Fragment>
                    : <Fragment key={`${item.id}_fragment_deleted`}></Fragment>
                  }
                </Fragment>
              );
            })}
          </tbody >
        </Table >
        <UserGuideForm onCreate={onCreateUserGuideToApi} onUpdate={onUpdateUserGuideToApi} />
        <DeleteConfirmation 
          title={currentUserGuide.title} 
          description="Are you sure you want to delete the following user guide"
          showFlag={deleteUserGuideModalShowFlag} 
          onConfirmation={onDeleteUserGuideConfirmation} 
          onClose={deleteUserGuideModalClose} />
      </div >
    </>
  );
};

export default Resources;
